import React from 'react';
import { Wallet, Laptop, Translate, CaretLeft, CaretRight, MapPin, TrendUp, TrendDown } from '@phosphor-icons/react';
import { Link } from './Link';
import { buildLocationSrpPath } from '../utils/location/build-location-srp-path';
import type { MarketHeaderData } from '../hooks/useMarketHeaderData';
import { Msg } from '../i18n-review/client/Msg';
import { HubCrossLinks } from './hub/HubCrossLinks';
import type { HubCrossLink } from '../types/hub-cross-link';
import { hubLinksHeading } from '../utils/seo/hub-index-links';

export interface MarketRailProps {
  data: MarketHeaderData;
  hubLinks?: HubCrossLink[];
  className?: string;
}

/** Icon badge + accent colors per stat section — same warm palette the old insight cards used. */
const ACCENTS = {
  salary: { badgeBg: 'bg-[#F5EDD0] dark:bg-[#2A1E08]', icon: 'text-[#6E4F00] dark:text-[#D4A820]' },
  workModel: { badgeBg: 'bg-[#C8DDCE] dark:bg-[#1A2E22]', icon: 'text-[#3B5E45] dark:text-[#98CFA8]' },
  language: { badgeBg: 'bg-[#DDD5C8] dark:bg-[#2A1D0F]', icon: 'text-[#5A3E20] dark:text-[#D4B898]' },
};

function IconBadge({ accent, children }: { accent: keyof typeof ACCENTS; children: React.ReactNode }): React.ReactElement {
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${ACCENTS[accent].badgeBg} ${ACCENTS[accent].icon} shrink-0`}>
      {children}
    </span>
  );
}

function TrendBadge({ value, baseline }: { value: number; baseline: number }): React.ReactElement | null {
  if (value === baseline) return null;
  const up = value > baseline;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
        up
          ? 'bg-[#E4EEE7] text-[#3B5E45] dark:bg-[#1A2E22] dark:text-[#98CFA8]'
          : 'bg-[#F7ECD8] text-[#6E4F00] dark:bg-[#2A1E08] dark:text-[#D4A820]'
      }`}
    >
      {up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
    </span>
  );
}

/**
 * Desktop presentation: a pull-out side tab beside the results grid. Expanded, it's the full rail
 * card (photo header, then salary/work-model/language stats with Finland-comparison chips,
 * overview prose, and nearby-city links, as hairline-divided sections); collapsed, it shrinks to a
 * slim vertical tab (location name + expand affordance) and the results grid reflows to fill the
 * reclaimed width, since collapse/expand animates the <aside>'s own width, not just inner content
 * height. Default expanded. Both the tab and the full card are always in the DOM (curl-verifiable
 * for SEO) — the inactive one is hidden with opacity/pointer-events, never unmounted or
 * display:none, so the location's market text is present in the server-rendered HTML regardless
 * of which state a given visitor leaves it in.
 */
export function MarketRail({ data, hubLinks, className = '' }: MarketRailProps): React.ReactElement {
  const {
    intl,
    formatMessage,
    locale,
    effectiveBackgroundImage,
    backgroundImageAlt,
    resultsHeadingText,
    translatedLocation,
    sampleCount,
    description,
    locationHeaderText,
    salaryValue,
    salaryRange,
    finlandAverages,
    avgSalaryNum,
    workModelData,
    languageData,
    remotePercentage,
    nearbyLocations,
    lastUpdatedDate,
    handleRemoveLocation,
  } = data;

  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [backgroundImageLoadFailed, setBackgroundImageLoadFailed] = React.useState(false);

  React.useEffect(() => {
    setBackgroundImageLoadFailed(false);
  }, [effectiveBackgroundImage?.src]);

  // overflow-hidden only needs to be on while the width is actively animating, to clip the
  // reveal/collapse transition — left on permanently, it clips the tab/card's own box-shadow
  // flush against their rounded corners (blur paints into the bounding box's square corners,
  // then gets cut off before it can fade out into a round halo), which reads as a square shadow.
  const isFirstRender = React.useRef(true);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), 300);
    return () => clearTimeout(timer);
  }, [isCollapsed]);

  const isShowingBackgroundImage = Boolean(effectiveBackgroundImage && !backgroundImageLoadFailed);
  const englishPercent = languageData.total > 0 ? Math.round((languageData.english / languageData.total) * 100) : null;

  const sectionClass = 'px-5 py-4 border-t border-[#E3DCD0] dark:border-[#332C24] first:border-t-0';
  const sectionHeadClass = 'flex items-center justify-between gap-2 mb-2';
  const sectionLabelClass = 'flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]';
  const compareLineClass = 'mt-1.5 text-[11px] text-[#7A6E5F] dark:text-[#B8A48C] flex items-center gap-1.5';

  const toggleAriaLabel = formatMessage({
    id: 'marketHeader.toggle',
    defaultMessage: 'Toggle market header',
    description: 'Button aria-label to show/hide market statistics header section',
  });

  return (
    <aside
      className={`hidden lg:block shrink-0 transition-[width] duration-300 ease-in-out ${
        isTransitioning ? 'overflow-hidden' : 'overflow-visible'
      } ${isCollapsed ? 'w-14' : 'w-[320px]'} ${className}`}
      aria-labelledby="location-header-title-rail"
    >
      <div className="sticky top-24 relative">
        {/* Collapsed pull-out tab — always in the DOM; only visually active (opacity) while collapsed. */}
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          className={`w-14 rounded-[1.75rem] border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] shadow-[0_1px_2px_rgba(30,26,20,0.04),0_10px_28px_rgba(30,26,20,0.07)] dark:shadow-none flex flex-col items-center gap-2.5 py-5 cursor-pointer transition-opacity duration-200 ${
            isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none absolute inset-0'
          }`}
          aria-label={toggleAriaLabel}
          aria-expanded={false}
        >
          <MapPin size={14} weight="fill" className="text-[#3B5E45] dark:text-[#98CFA8]" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#3D3529] dark:text-[#C4A882] [writing-mode:vertical-rl] rotate-180">
            {translatedLocation}
          </span>
          <CaretRight size={14} weight="bold" className="text-[#7A6E5F] dark:text-[#B8A48C]" />
        </button>

        {/* Full card — always in the DOM (SEO); visually hidden (opacity, not unmounted) while collapsed to the tab above. */}
        <div
          className={`w-[320px] rounded-[1.75rem] overflow-hidden border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] shadow-[0_1px_2px_rgba(30,26,20,0.04),0_10px_28px_rgba(30,26,20,0.07)] dark:shadow-none transition-opacity duration-200 ${
            isCollapsed ? 'opacity-0 pointer-events-none absolute inset-0' : 'opacity-100'
          }`}
          aria-hidden={isCollapsed}
        >
        {/* Photo header */}
        <div className="relative">
          {isShowingBackgroundImage && effectiveBackgroundImage ? (
            <div className="relative h-32 overflow-hidden">
              <img
                src={effectiveBackgroundImage.src}
                srcSet={effectiveBackgroundImage.srcset || undefined}
                sizes="320px"
                alt={backgroundImageAlt}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={() => setBackgroundImageLoadFailed(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/0" />
            </div>
          ) : (
            <div className="h-32 bg-gradient-to-br from-[#3B5E45] via-[#2F4D3A] to-[#1E1A14] dark:from-[#22322A] dark:via-[#1A2620] dark:to-[#170F07]" />
          )}
          <div className="absolute inset-0 flex flex-col justify-end p-4">
            {/* Text sits over the bottom black/70 gradient, so white is always legible: an
                earlier per-image brightness detection sampled the top of the photo and flipped
                bright-sky images to unreadable dark-on-dark. */}
            <div className="flex items-center gap-1 mb-0.5">
              <MapPin size={12} weight="fill" className="text-white/70" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-white/70">
                {translatedLocation}
              </span>
            </div>
            <p
              id="location-header-title-rail"
              aria-hidden="true"
              className="text-xl font-bold tracking-tight leading-tight text-white"
            >
              {resultsHeadingText}
            </p>
            <p className="text-xs mt-0.5 text-white/80">
              {sampleCount > 0 ? (
                <Msg
                  id="marketHeader.rail.samplesUpdatedDaily"
                  defaultMessage="{count} open jobs · updated daily"
                  values={{ count: intl.formatNumber(sampleCount) }}
                  description="Rail card subtitle: sample count and freshness cadence under the location title"
                />
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 transition-colors cursor-pointer"
            aria-label={toggleAriaLabel}
            aria-expanded={true}
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        </div>

        {/* Hairline-divided sections. The whole card (this included) hides/shows as one unit via
            the opacity toggle above when the tab collapses, so these no longer need their own
            independent collapse mechanism — always rendered, same as the photo header. */}
        <div>
          {(description || locationHeaderText.overviewText) && (
            <div className={sectionClass.replace('first:border-t-0', '')}>
              <p className="text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {description && !locationHeaderText.overviewText ? description : locationHeaderText.overviewText}
              </p>
            </div>
          )}

          {/* Salary */}
          <div className={sectionClass}>
            <div className={sectionHeadClass}>
              <span className={sectionLabelClass}>
                <IconBadge accent="salary">
                  <Wallet size={14} weight="bold" />
                </IconBadge>
                <Msg id="marketHeader.salary.label" defaultMessage="Salary Trends" description="Label for salary trends card in market header" />
              </span>
              {finlandAverages?.avgSalaryNum && finlandAverages.avgSalaryNum > 0 && avgSalaryNum > 0 && (
                <TrendBadge value={avgSalaryNum} baseline={finlandAverages.avgSalaryNum} />
              )}
            </div>
            {salaryValue && <div className="text-2xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF]">{salaryValue}</div>}
            {salaryRange && (
              <div className="mt-1 text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                €{Math.round(salaryRange.low / 1000)}k - €{Math.round(salaryRange.high / 1000)}k/mo{' '}
                <span className="text-xs font-normal text-[#7A6E5F] dark:text-[#B8A48C]">
                  {salaryRange.isPercentile ? (
                    <Msg id="marketHeader.salary.rangePercentile" defaultMessage="25th-75th percentile range" description="Label for salary range showing 25th to 75th percentile" />
                  ) : (
                    <Msg id="marketHeader.salary.range" defaultMessage="Salary range" description="Label for salary range (min-max)" />
                  )}
                </span>
              </div>
            )}
            {finlandAverages?.avgSalaryNum && finlandAverages.avgSalaryNum > 0 && avgSalaryNum > 0 && (
              <div className={compareLineClass}>
                <Msg
                  id="marketHeader.salary.finlandComparison"
                  defaultMessage="Finland avg: {finlandAvg}"
                  values={{ finlandAvg: finlandAverages.avgSalaryFormatted || `€${Math.round(finlandAverages.avgSalaryNum / 1000)}k` }}
                  description="Text showing Finland average salary for comparison with location-specific salary"
                />
              </div>
            )}
            {locationHeaderText.salaryBlurb && (
              <p className="mt-2 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">{locationHeaderText.salaryBlurb}</p>
            )}
          </div>

          {/* Work model */}
          <div className={sectionClass}>
            <div className={sectionHeadClass}>
              <span className={sectionLabelClass}>
                <IconBadge accent="workModel">
                  <Laptop size={14} weight="bold" />
                </IconBadge>
                <Msg id="marketHeader.workModel.label" defaultMessage="Work Model" description="Section label for work model statistics (remote, hybrid, onsite) in market header" />
              </span>
              {finlandAverages?.remotePercentage !== undefined && remotePercentage > 0 && (
                <TrendBadge value={remotePercentage} baseline={finlandAverages.remotePercentage} />
              )}
            </div>
            <div className="text-lg font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF]">
              {workModelData.total > 0 && workModelData.headline ? (
                <Msg id={workModelData.headline} defaultMessage={workModelData.headlineDefault} description="Dynamic headline describing work model distribution (e.g., 'Primarily Onsite', 'Remote-Friendly')" />
              ) : (
                <Msg id="marketHeader.workModel.noData" defaultMessage="Flexible" description="Fallback text shown when work model distribution data is not available, indicating flexible work options" />
              )}
            </div>
            {workModelData.total > 0 && workModelData.breakdown && (
              <div className="mt-2 h-2 rounded-full overflow-hidden flex bg-[#DDD5C8] dark:bg-[#4A3520]" aria-hidden>
                {workModelData.breakdown.onsitePercent ? <div className="bg-[#3B5E45] dark:bg-[#5A8A68]" style={{ width: `${workModelData.breakdown.onsitePercent}%` }} /> : null}
                {workModelData.breakdown.hybridPercent ? <div className="bg-[#5A8A68] dark:bg-[#98CFA8]" style={{ width: `${workModelData.breakdown.hybridPercent}%` }} /> : null}
                {workModelData.breakdown.remotePercent ? <div className="bg-[#90BC9C] dark:bg-[#B6D4BC]" style={{ width: `${workModelData.breakdown.remotePercent}%` }} /> : null}
              </div>
            )}
            {finlandAverages?.remotePercentage !== undefined && remotePercentage > 0 && (
              <div className={compareLineClass}>
                <Msg
                  id="marketHeader.workModel.finlandComparison"
                  defaultMessage="Finland: {finlandRemote}% remote/hybrid"
                  description="Comparison text showing Finland-wide remote/hybrid work percentage, where {finlandRemote} is the percentage"
                  values={{ finlandRemote: finlandAverages.remotePercentage }}
                />
              </div>
            )}
            {locationHeaderText.workModelBlurb && (
              <p className="mt-2 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">{locationHeaderText.workModelBlurb}</p>
            )}
          </div>

          {/* Language */}
          <div className={sectionClass}>
            <div className={sectionHeadClass}>
              <span className={sectionLabelClass}>
                <IconBadge accent="language">
                  <Translate size={14} weight="bold" />
                </IconBadge>
                <Msg id="marketHeader.language.label" defaultMessage="Language Demand" description="Section label for language requirement statistics in market header" />
              </span>
              {finlandAverages?.englishLanguagePercentage !== undefined && englishPercent !== null && (
                <TrendBadge value={englishPercent} baseline={finlandAverages.englishLanguagePercentage} />
              )}
            </div>
            <div className="text-lg font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF]">
              {englishPercent !== null ? (
                <>
                  {englishPercent}% <Msg id="marketHeader.language.english" defaultMessage="English" description="Label for English language requirement category in language distribution chart" />
                </>
              ) : (
                <Msg id="marketHeader.language.mixed" defaultMessage="Mixed" description="Label for mixed language requirement category (both English and local language) in language distribution chart" />
              )}
            </div>
            {finlandAverages?.englishLanguagePercentage !== undefined && englishPercent !== null && (
              <div className={compareLineClass}>
                <Msg
                  id="marketHeader.language.finlandComparison"
                  defaultMessage="Finland: {finlandEnglish}% English"
                  values={{ finlandEnglish: finlandAverages.englishLanguagePercentage }}
                  description="Text showing Finland average English language percentage for comparison"
                />
              </div>
            )}
            {locationHeaderText.languageBlurb && (
              <p className="mt-2 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">{locationHeaderText.languageBlurb}</p>
            )}
          </div>

          {/* Nearby markets — visible anchors (previously JSON-LD-only significantLinks). */}
          {nearbyLocations.length > 0 && (
            <div className={sectionClass}>
              <div className={sectionLabelClass}>
                <Msg id="marketHeader.nearbyMarkets" defaultMessage="Nearby markets" description="Heading above links to nearby city job markets in the rail card" />
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {nearbyLocations.map((city) => (
                  <Link
                    key={city.slug}
                    href={buildLocationSrpPath(city.slug, locale)}
                    locale={locale}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#3B5E45] dark:text-[#98CFA8] bg-[#F2EADF] dark:bg-[#2A1D0F] rounded-full px-2.5 py-1 hover:bg-[#E4EEE7] dark:hover:bg-[#1A2E22] transition-colors"
                  >
                    <MapPin size={10} weight="fill" className="opacity-60" />
                    {city.displayName}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Role x city pages, employers in the city, roles and salary indexes: the city hub's own crawl paths. */}
          {hubLinks && hubLinks.length > 0 && (
            <div className={sectionClass}>
              <HubCrossLinks
                variant="chips"
                links={hubLinks}
                locale={locale as 'en' | 'fi' | 'sv'}
                heading={hubLinksHeading(locale as 'en' | 'fi' | 'sv')}
                headingLevel="h3"
              />
            </div>
          )}

          <div className={`${sectionClass} flex items-center justify-between gap-2 text-xs text-[#7A6E5F] dark:text-[#B8A48C]`}>
            {lastUpdatedDate ? (
              <span>
                <Msg id="marketHeader.updated" defaultMessage="Updated:" description="Label for the last updated date of market data" />{' '}
                <time dateTime={lastUpdatedDate.toISOString()}>
                  {intl.formatDate(lastUpdatedDate, { year: 'numeric', month: 'short', day: 'numeric' })}
                </time>
              </span>
            ) : <span />}
            <button
              type="button"
              onClick={handleRemoveLocation}
              className="font-medium text-[#3B5E45] dark:text-[#98CFA8] hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-colors cursor-pointer"
            >
              <Msg id="marketHeader.removeLocation" defaultMessage="Remove location" description="Button label to remove the active location filter" />
            </button>
          </div>
        </div>
        </div>
      </div>
    </aside>
  );
}
